import React from 'react';
import { Box, Typography } from '@mui/material';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { Right } from '../types/roles';
import RightsDroppableList from './RightsDroppableList';
import RightsTransferButtons from './RightsTransferButtons';

interface RightsAssignmentSectionProps {
  availableRights: Right[];
  setAvailableRights: React.Dispatch<React.SetStateAction<Right[]>>;
  assignedRights: Right[];
  setAssignedRights: React.Dispatch<React.SetStateAction<Right[]>>;
  selectedAvailableRights: string[];
  setSelectedAvailableRights: React.Dispatch<React.SetStateAction<string[]>>;
  selectedAssignedRights: string[];
  setSelectedAssignedRights: React.Dispatch<React.SetStateAction<string[]>>;
  moveToAssigned: (rights: Right[]) => void;
  moveToAvailable: (rights: Right[]) => void;
}

const RightsAssignmentSection: React.FC<RightsAssignmentSectionProps> = ({
  availableRights,
  assignedRights,
  selectedAvailableRights,
  setSelectedAvailableRights,
  selectedAssignedRights,
  setSelectedAssignedRights,
  moveToAssigned,
  moveToAvailable,
}) => {
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination || (source.droppableId === destination.droppableId && source.index === destination.index)) {
      return;
    }

    if (source.droppableId === 'available' && destination.droppableId === 'assigned') {
      const right = availableRights[source.index];
      moveToAssigned([right]);
    }

    if (source.droppableId === 'assigned' && destination.droppableId === 'available') {
      const right = assignedRights[source.index];
      moveToAvailable([right]);
    }
  };

  const handleSelectAvailable = (id: string) => {
    if (selectedAvailableRights.includes(id)) {
      setSelectedAvailableRights(selectedAvailableRights.filter((rightId) => rightId !== id));
    } else {
      setSelectedAvailableRights([...selectedAvailableRights, id]);
    }
  };

  const handleSelectAssigned = (id: string) => {
    if (selectedAssignedRights.includes(id)) {
      setSelectedAssignedRights(selectedAssignedRights.filter((rightId) => rightId !== id));
    } else {
      setSelectedAssignedRights([...selectedAssignedRights, id]);
    }
  };

  const handleMoveToAssigned = () => {
    const toMove = availableRights.filter((right) => selectedAvailableRights.includes(right.id));
    moveToAssigned(toMove);
  };

  const handleMoveToAvailable = () => {
    const toMove = assignedRights.filter((right) => selectedAssignedRights.includes(right.id));
    moveToAvailable(toMove);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ color: (theme) => theme.palette.custom.titleText }}>
        Rights Assignment
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: (theme) => theme.palette.custom.titleText }}>
              Available Rights
            </Typography>
            <RightsDroppableList
              rights={availableRights}
              selectedRights={selectedAvailableRights}
              droppableId="available"
              onSelectRight={handleSelectAvailable}
            />
          </Box>

          <RightsTransferButtons
            selectedAvailableRights={selectedAvailableRights}
            selectedAssignedRights={selectedAssignedRights}
            onMoveToAssigned={handleMoveToAssigned}
            onMoveToAvailable={handleMoveToAvailable}
          />

          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: (theme) => theme.palette.custom.titleText }}>
              Assigned Rights
            </Typography>
            <RightsDroppableList
              rights={assignedRights}
              selectedRights={selectedAssignedRights}
              droppableId="assigned"
              onSelectRight={handleSelectAssigned}
            />
          </Box>
        </Box>
      </DragDropContext>
    </>
  );
};

export default RightsAssignmentSection;